import { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, Quote, MapPin } from 'lucide-react';
import { API } from '@/App';

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials/public`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
        </div>
      </section>
    );
  }

  // Don't show section if no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const getDisplayName = (name) => {
    // Show first name and last initial for privacy
    if (!name) return 'Anonymous';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[1].charAt(0)}.`;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-primary-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-serif font-bold text-secondary-900 mb-4">
            What Our Graduates Are Saying
          </h2>
          <p className="text-secondary-600 text-lg max-w-2xl mx-auto">
            Real stories from real women who completed the Radiant 40 program
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.slice(0, 6).map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start gap-4 mb-4">
                {testimonial.photo_url ? (
                  <img
                    src={`${API.replace('/api', '')}${testimonial.photo_url}`}
                    alt={getDisplayName(testimonial.display_name)}
                    className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                    <span className="text-xl font-semibold text-primary">
                      {testimonial.display_name?.charAt(0) || '?'}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {getDisplayName(testimonial.display_name)}
                  </h4>
                  {(testimonial.display_city || testimonial.display_state) && (
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {[testimonial.display_city, testimonial.display_state]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                  <div className="mt-1">
                    {renderStars(testimonial.rating)}
                  </div>
                </div>
              </div>

              <div className="relative">
                <Quote className="w-8 h-8 text-primary/10 absolute -top-2 -left-2" />
                <p className="text-gray-700 leading-relaxed pl-4">
                  "{testimonial.testimonial_text}"
                </p>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-primary font-medium">
                  Week {testimonial.week_completed} Graduate
                </span>
              </div>
            </div>
          ))}
        </div>

        {testimonials.length > 6 && (
          <div className="text-center mt-10">
            <p className="text-secondary-600">
              And {testimonials.length - 6} more success stories...
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
